/**
 * ApprovalDetailsView — shared component for rendering approval `requested_data`.
 *
 * Used by:
 *   - pages/admin/ApprovalsPage.jsx     (admin view: approve/reject)
 *   - pages/MyApprovalsPage.jsx         (worker view: see own submissions)
 *
 * Single source of truth. Add a new field label here once → both pages benefit.
 */

import React, { useState } from 'react';
import {
  User, Phone, FileText, Calendar, DollarSign, Tag,
  Package, Hash, Truck, Percent, MapPin, Image, ExternalLink,
  CreditCard, Layers, FolderOpen
} from 'lucide-react';

export const TYPE_ICONS = {
  PROJECT:   '📁',
  ITEM:      '📦',
  CHALLAN:   '🚚',
  PO_STATUS: '📄',
  SALE:      '💰',
  MAIN_PO:   '📋',
  CASHBOOK_ADVANCE: '💵',
  CASHBOOK_EXPENSE: '🧾',
  QUALITY_REPORT: '✅',
  CFT: '📐',
};

export const TYPE_LABELS = {
  PROJECT:   'Project Creation',
  ITEM:      'Item Addition',
  CHALLAN:   'Sale Challan (Delivery)',
  PO_STATUS: 'PO Status Update',
  SALE:      'Sale / Invoice',
  MAIN_PO:   'Manufacturing Order',
  CASHBOOK_ADVANCE: 'Cash Book — Advance Request',
  CASHBOOK_EXPENSE: 'Cash Book — Expense',
  QUALITY_REPORT: 'Quality Report',
  CFT: 'CFT Sheet',
};

// Friendly labels + icons for every body field across all approval types
const FIELD_LABELS = {
  // PROJECT
  name:            { label: 'Project Name',    icon: Tag },
  client_name:     { label: 'Client Name',     icon: User },
  client_phone:    { label: 'Client Phone',    icon: Phone },
  client_address:  { label: 'Client Address',  icon: MapPin },
  client_gstin:    { label: 'Client GSTIN',    icon: CreditCard },
  description:     { label: 'Description',     icon: FileText },
  priority:        { label: 'Priority',        icon: Tag },
  order_date:      { label: 'Order Date',      icon: Calendar },
  deadline:        { label: 'Deadline',        icon: Calendar },
  total_amount:    { label: 'Total Amount',    icon: DollarSign },
  notes:           { label: 'Notes',           icon: FileText },
  is_ready:        { label: 'Ready to Start',  icon: Tag },
  // ITEM
  project_name:    { label: 'Project',         icon: FolderOpen },
  project_id:      { label: 'Project ID',      icon: Hash },
  item_name:       { label: 'Item Name',       icon: Package },
  proto_code:      { label: 'Proto Code',      icon: Hash },
  quantity:        { label: 'Quantity',        icon: Package },
  unit:            { label: 'Unit',            icon: Tag },
  rate:            { label: 'Rate',            icon: DollarSign },
  unit_price:      { label: 'Unit Price',      icon: DollarSign },
  material:        { label: 'Material',        icon: Layers },
  dimensions:      { label: 'Dimensions',      icon: Tag },
  // CHALLAN / SALE
  challan_type:    { label: 'Type',            icon: FileText },
  challan_date:    { label: 'Challan Date',    icon: Calendar },
  delivery_date:   { label: 'Delivery Date',   icon: Calendar },
  manual_number:   { label: 'Challan Number',  icon: Hash },
  transport_name:  { label: 'Transport',       icon: Truck },
  vehicle_number:  { label: 'Vehicle No.',     icon: Truck },
  vehicle_length:  { label: 'Vehicle Length',  icon: Truck },
  tax_percent:     { label: 'Tax %',           icon: Percent },
  discount_amount: { label: 'Discount',        icon: DollarSign },
  cpo_id:          { label: 'Client PO ID',    icon: Hash },
  subtotal:        { label: 'Subtotal',        icon: DollarSign },
  tax_amount:      { label: 'Tax Amount',      icon: DollarSign },
  cgst:            { label: 'CGST %',          icon: Percent },
  sgst:            { label: 'SGST %',          icon: Percent },
  igst:            { label: 'IGST %',          icon: Percent },
  status:          { label: 'Status',          icon: Tag },
  // PO_STATUS
  po_id:           { label: 'PO ID',           icon: Hash },
};

const CURRENCY_FIELDS  = new Set(['total_amount', 'rate', 'unit_price', 'discount_amount', 'subtotal', 'tax_amount']);
const PERCENT_FIELDS   = new Set(['tax_percent', 'cgst', 'sgst', 'igst']);
const TITLECASE_FIELDS = new Set(['priority', 'status', 'challan_type']);

// Skip these from scalar display (handled separately)
const SKIP_FIELDS = new Set(['items', 'image_urls']);
// project_id is shown as project_name (enriched by backend) — hide raw id when name present
function shouldSkipField(key, data) {
  if (SKIP_FIELDS.has(key)) return true;
  if (key === 'project_id' && data?.project_name) return true;
  return false;
}

const PRIORITY_COLORS = {
  high:   'bg-red-100 text-red-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low:    'bg-green-100 text-green-700',
};

const CHALLAN_TYPE_LABELS = {
  delivery:  '🚚 Delivery Challan',
  sale:      '💰 Sale Invoice',
  proforma:  '📋 Proforma Invoice',
};

function formatValue(key, val) {
  if (val === null || val === undefined) return '—';
  if (CURRENCY_FIELDS.has(key) && val !== '' && !isNaN(val)) {
    return `₹ ${Number(val).toLocaleString('en-IN')}`;
  }
  if (PERCENT_FIELDS.has(key) && val !== '' && !isNaN(val)) return `${val}%`;
  if (key === 'is_ready') return val ? 'Yes ✅' : 'No';
  if (key === 'challan_type') return CHALLAN_TYPE_LABELS[val] || val;
  if (TITLECASE_FIELDS.has(key) && typeof val === 'string' && val.length) {
    return val.charAt(0).toUpperCase() + val.slice(1);
  }
  return String(val);
}

/**
 * Extracts a short human-readable label for the approval card header.
 */
export function getApprovalDisplayName(data) {
  if (!data || typeof data !== 'object') return null;
  const body = (data.body && typeof data.body === 'object') ? data.body : data;
  return body.name || body.item_name || body.client_name || null;
}

// ── Image thumbnail component ─────────────────────────────────────────────────
function ImageGallery({ images }) {
  const [lightbox, setLightbox] = useState(null);

  if (!images || images.length === 0) return null;

  return (
    <div className="bg-white p-3 rounded-lg border border-gray-100">
      <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
        <Image className="w-3.5 h-3.5 text-blue-400" />
        Reference Images / Files ({images.length})
      </p>
      <div className="flex flex-wrap gap-2">
        {images.map((imgObj, idx) => {
          const url = typeof imgObj === 'string' ? imgObj : (imgObj.url || imgObj.image_path || '');
          const resType = typeof imgObj === 'object' ? (imgObj.resource_type || 'image') : 'image';
          const isPdf = resType === 'raw' || url.toLowerCase().includes('.pdf');
          const name = typeof imgObj === 'object' ? (imgObj.original_name || `File ${idx + 1}`) : `Image ${idx + 1}`;

          if (isPdf) {
            return (
              <a
                key={idx}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 hover:bg-red-100 transition"
              >
                <FileText className="w-3.5 h-3.5" />
                {name}
                <ExternalLink className="w-3 h-3 ml-1 opacity-60" />
              </a>
            );
          }

          return (
            <button
              key={idx}
              onClick={() => setLightbox(url)}
              className="relative group"
              title={name}
            >
              <img
                src={url}
                alt={name}
                className="w-20 h-20 object-cover rounded-lg border border-gray-200 hover:border-blue-400 transition shadow-sm group-hover:shadow-md"
                onError={e => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition flex items-center justify-center">
                <ExternalLink className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition" />
              </div>
            </button>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <img
            src={lightbox}
            alt="Full view"
            className="max-w-full max-h-[90vh] rounded-xl shadow-2xl object-contain"
            onClick={e => e.stopPropagation()}
          />
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full w-9 h-9 flex items-center justify-center text-lg hover:bg-opacity-80"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Main renderer. Expects the parsed `requested_data` blob:
 *   { method, path, body: {...form fields..., image_urls: [...] }, query, params }
 * Backward-compatible with legacy flat shape (no `body` key).
 */
export default function ApprovalDetailsView({ data, type, showMeta = true }) {
  if (!data || typeof data !== 'object') return null;

  const hasWrappedShape = data.body && typeof data.body === 'object' && !Array.isArray(data.body);
  const body   = hasWrappedShape ? data.body : data;
  const method = hasWrappedShape ? data.method : undefined;
  const path   = hasWrappedShape ? data.path   : undefined;
  const params = hasWrappedShape ? (data.params || {}) : {};

  // Line items (sale challan / sale)
  const items     = Array.isArray(body.items) ? body.items : null;
  // Images (pre-uploaded temp images stored as URLs in approval body)
  const imageUrls = Array.isArray(body.image_urls) ? body.image_urls : null;

  // Scalar fields: skip nested objects/arrays/empty
  const entries = Object.entries(body).filter(([key, val]) => {
    if (shouldSkipField(key, body)) return false;
    if (val === '' || val === null || val === undefined) return false;
    if (typeof val === 'object') return false;
    if (key === 'is_ready' && !val) return false;
    return true;
  });

  // For PO_STATUS the actual PO id lives in params.id, not body
  if (type === 'PO_STATUS' && params.id != null && !entries.find(([k]) => k === 'po_id')) {
    entries.unshift(['po_id', params.id]);
  }

  const hasAnything = entries.length > 0 || (items && items.length > 0) || (imageUrls && imageUrls.length > 0);
  if (!hasAnything) return <p className="text-sm text-gray-400">No details provided.</p>;

  return (
    <div className="space-y-3">
      {/* Method + path metadata chip */}
      {showMeta && (method || path) && (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {method && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded font-mono">{method}</span>
          )}
          {path && <span className="font-mono truncate">{path}</span>}
        </div>
      )}

      {/* Scalar fields grid */}
      {entries.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {entries.map(([key, val]) => {
            const config  = FIELD_LABELS[key];
            const Icon    = config?.icon || Tag;
            const label   = config?.label
              || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            const display = formatValue(key, val);

            // CFT sheet that takes wood stock below zero (advice only — approving is still allowed)
            if (key === 'stock_warning') {
              return (
                <div key={key} className="sm:col-span-2 flex items-start gap-2 bg-red-50 p-2 rounded-lg border border-red-200">
                  <Icon className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-red-600 font-semibold">Stock Warning</p>
                    <p className="text-sm text-red-800 break-words">{display}</p>
                  </div>
                </div>
              );
            }

            // Priority badge styling
            if (key === 'priority') {
              const colorClass = PRIORITY_COLORS[String(val).toLowerCase()] || 'bg-gray-100 text-gray-700';
              return (
                <div key={key} className="flex items-start gap-2 bg-white p-2 rounded-lg border border-gray-100">
                  <Icon className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400 font-medium">{label}</p>
                    <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs font-bold ${colorClass}`}>
                      {display}
                    </span>
                  </div>
                </div>
              );
            }

            return (
              <div key={key} className="flex items-start gap-2 bg-white p-2 rounded-lg border border-gray-100">
                <Icon className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-400 font-medium">{label}</p>
                  <p className="text-sm text-gray-800 font-semibold break-words">{display}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reference Images */}
      {imageUrls && imageUrls.length > 0 && (
        <ImageGallery images={imageUrls} />
      )}

      {/* Items table (sale challan / sale) */}
      {items && items.length > 0 && (
        <div className="bg-white p-3 rounded-lg border border-gray-100">
          <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
            <Package className="w-3.5 h-3.5" /> Items ({items.length})
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left py-1 px-2 font-medium">Item</th>
                  <th className="text-right py-1 px-2 font-medium">Qty</th>
                  <th className="text-left py-1 px-2 font-medium">Unit</th>
                  <th className="text-right py-1 px-2 font-medium">Rate</th>
                  <th className="text-right py-1 px-2 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const qty   = Number(it.quantity) || 0;
                  const rate  = Number(it.rate) || 0;
                  const total = qty * rate;
                  return (
                    <tr key={idx} className="border-b border-gray-50 last:border-0">
                      <td className="py-1.5 px-2 text-gray-700 font-medium">{it.item_name || '—'}</td>
                      <td className="py-1.5 px-2 text-right text-gray-700">{qty}</td>
                      <td className="py-1.5 px-2 text-gray-600">{it.unit || 'pcs'}</td>
                      <td className="py-1.5 px-2 text-right text-gray-700">₹ {rate.toLocaleString('en-IN')}</td>
                      <td className="py-1.5 px-2 text-right text-gray-800 font-semibold">₹ {total.toLocaleString('en-IN')}</td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Grand total row */}
              {items.length > 1 && (
                <tfoot>
                  <tr className="border-t-2 border-gray-200">
                    <td colSpan={4} className="py-1.5 px-2 text-right text-xs font-semibold text-gray-600">Grand Total</td>
                    <td className="py-1.5 px-2 text-right text-sm font-bold text-gray-900">
                      ₹ {items.reduce((sum, it) => sum + ((Number(it.quantity) || 0) * (Number(it.rate) || 0)), 0).toLocaleString('en-IN')}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
